import {RequestResult, RequestResultValue} from "../../common/api/requestResult";
import {HttpService} from "../../common/api/httpService";
import {PaginationResponse} from "../../models/paginationResponse";
import {
    AutoHoldCaseRow,
    AutoHoldDictionaryRow,
    AutoHoldHitRow,
    AutoHoldKpi,
    AutoHoldPreviewRow,
    AutoHoldRuleRow,
    AutoHoldSignalSchema,
    AutoHoldWhitelistRow,
} from "../../models/autoHold";

const http = () => HttpService.getInstance();

/** KPI автохолда за текущие сутки UTC. */
export async function autoHoldKpiApi(): Promise<RequestResultValue<AutoHoldKpi>> {
    const result = await http().adminFetch(`AutoHold/kpi`, "GET");
    return result.result
        ? new RequestResultValue(true, result.value as AutoHoldKpi)
        : new RequestResultValue(false);
}

/** Постраничный список кейсов очереди разбора. */
export async function autoHoldCasesApi(page: number, count: number, status?: number)
    : Promise<RequestResultValue<PaginationResponse<AutoHoldCaseRow>>> {
    const result = await http().adminFetch(`AutoHold/cases/list`, "POST", { page, count, status: status ?? null });
    return result.result
        ? new RequestResultValue(true, result.value as PaginationResponse<AutoHoldCaseRow>)
        : new RequestResultValue(false);
}

/** Вердикт: оставить блок, пометить ошибкой; removeBlock снимает флаги сработавших правил. */
export async function autoHoldVerdictApi(
    caseId: number,
    status: number,
    comment: string,
    addToWhitelist: boolean,
    removeBlock = false,
): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/cases/verdict`, "POST", {
        caseId,
        status,
        comment,
        addToWhitelist,
        removeBlock,
    });
    return new RequestResult(result.result);
}

/** Список правил со статистикой. */
export async function autoHoldRulesApi(): Promise<RequestResultValue<AutoHoldRuleRow[]>> {
    const result = await http().adminFetch(`AutoHold/rules`, "GET");
    return result.result
        ? new RequestResultValue(true, result.value as AutoHoldRuleRow[])
        : new RequestResultValue(false);
}

/** Создаёт или обновляет правило. */
export async function autoHoldSaveRuleApi(body: Partial<AutoHoldRuleRow> & { rootJson: string; name: string }): Promise<RequestResultValue<AutoHoldRuleRow>> {
    const result = await http().adminFetch(`AutoHold/rules/save`, "POST", body);
    return result.result
        ? new RequestResultValue(true, result.value as AutoHoldRuleRow)
        : new RequestResultValue(false);
}

/** Мягко удаляет правило. */
export async function autoHoldDeleteRuleApi(id: number): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/rules/${id}/delete`, "POST");
    return new RequestResult(result.result);
}

/** Каталог сигналов для конструктора условий. */
export async function autoHoldSignalsApi(): Promise<RequestResultValue<AutoHoldSignalSchema[]>> {
    const result = await http().adminFetch(`AutoHold/signals`, "GET");
    return result.result
        ? new RequestResultValue(true, result.value as AutoHoldSignalSchema[])
        : new RequestResultValue(false);
}

/** Уникальные попадания пользователей в правило. */
export async function autoHoldHitsApi(ruleId: number, source?: number): Promise<RequestResultValue<AutoHoldHitRow[]>> {
    const q = typeof source === "number" ? `?source=${source}` : "";
    const result = await http().adminFetch(`AutoHold/rules/${ruleId}/hits${q}`, "GET");
    return result.result
        ? new RequestResultValue(true, result.value as AutoHoldHitRow[])
        : new RequestResultValue(false);
}

/** Помечает попадание как ошибку. removeBlock — снять флаги этого правила. */
export async function autoHoldHitFalsePositiveApi(
    ruleId: number,
    userId: string,
    removeBlock = false,
): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/rules/${ruleId}/hits/false-positive`, "POST", { userId, removeBlock });
    return new RequestResult(result.result);
}

/** Применяет флаги правила к одному попаданию. */
export async function autoHoldApplyHitApi(ruleId: number, userId: string): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/rules/${ruleId}/hits/apply`, "POST", { userId });
    return new RequestResult(result.result);
}

/** Ставит в очередь применение флагов ко всем неприменённым попаданиям. */
export async function autoHoldApplyAllApi(ruleId: number): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/rules/${ruleId}/hits/apply-all`, "POST");
    return new RequestResult(result.result);
}

/** Ручной прогон списка SteamId без применения флагов. Только выделенное правило. */
export async function autoHoldPreviewApi(ruleId: number, steamIds: string): Promise<RequestResultValue<AutoHoldPreviewRow[]>> {
    const result = await http().adminFetch(`AutoHold/rules/${ruleId}/preview`, "POST", { steamIds });
    return result.result
        ? new RequestResultValue(true, result.value as AutoHoldPreviewRow[])
        : new RequestResultValue(false);
}

/** Ставит в очередь прогон только этого правила по уже заблокированным. Флаги не вешаются. */
export async function autoHoldPreviewAllApi(ruleId: number): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/rules/${ruleId}/preview-all`, "POST");
    return new RequestResult(result.result);
}

/** Постраничный список исключений. */
export async function autoHoldWhitelistApi(page: number, count: number)
    : Promise<RequestResultValue<PaginationResponse<AutoHoldWhitelistRow>>> {
    const result = await http().adminFetch(`AutoHold/whitelist/list`, "POST", { page, count });
    return result.result
        ? new RequestResultValue(true, result.value as PaginationResponse<AutoHoldWhitelistRow>)
        : new RequestResultValue(false);
}

/** Добавляет пользователя в исключения. */
export async function autoHoldAddWhitelistApi(userId: string, reason: string): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/whitelist/add`, "POST", { userId, reason });
    return new RequestResult(result.result);
}

/** Убирает запись из исключений. */
export async function autoHoldRemoveWhitelistApi(id: number): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/whitelist/${id}/remove`, "POST");
    return new RequestResult(result.result);
}

/** Список справочников. */
export async function autoHoldDictionariesApi(): Promise<RequestResultValue<AutoHoldDictionaryRow[]>> {
    const result = await http().adminFetch(`AutoHold/dictionaries`, "GET");
    return result.result
        ? new RequestResultValue(true, result.value as AutoHoldDictionaryRow[])
        : new RequestResultValue(false);
}

/** Сохраняет значения справочника. */
export async function autoHoldSaveDictionaryApi(key: string, values: string[], description?: string): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/dictionaries/save`, "POST", { key, values, description });
    return new RequestResult(result.result);
}

/** Ставит в очередь бэкфилл по текущим блокировкам и отпечаткам реквизитов. */
export async function autoHoldBackfillApi(): Promise<RequestResult> {
    const result = await http().adminFetch(`AutoHold/backfill`, "POST");
    return new RequestResult(result.result);
}
